/**
 * lexer.ts —— 词法分析
 *
 * 把 IL 源码切成一串 token。IL 的语法极小，token 种类也少：
 *
 *   setting  .  setTheme  (  'dark'  )  ;
 *   └ident   └dot └ident  └lparen └str  └rparen └semi
 *
 * 为什么手写而不是用正则一把梭：
 *   正则没法可靠地处理「字符串里的括号」「转义引号」「注释边界」这些情况。
 *   手写状态机虽然多几十行，但每条规则都看得见、能单独测。
 *   而且最终要报出精确的字符位置（error.pos），正则匹配出来的位置很难用。
 *
 * 设计取舍：**不生成注释 token**。
 *   注释在清洗阶段（ai/sanitize.ts）就该被剥掉；如果模型绕过清洗
 *   直接塞了注释进来，Lexer 也顺手跳过 —— 但注释不进入 token 流，
 *   这样 Parser 完全不用关心注释的存在。
 */

import type { EvalError } from '@/types/il';

/** 源码中的一个位置区间 */
export interface Span {
  start: number;
  end: number;
}

export type TokenType =
  | 'ident' // 标识符：setting / setTheme
  | 'string' // 字符串字面量：'dark' / "文字"
  | 'number' // 数字字面量：1 / 0.875 / -3
  | 'boolean' // true / false
  | 'dot' // .
  | 'lparen' // (
  | 'rparen' // )
  | 'comma' // ,
  | 'semi' // ;
  | 'eof'; // 结束标记（Parser 靠它收尾）

export interface Token extends Span {
  type: TokenType;
  /** 原始文本（含引号，便于报错时展示） */
  raw: string;
  /** 解析后的值：string 类型的去引号内容、number 的数值、boolean 的真假 */
  value?: string | number | boolean;
}

/** 词法分析失败时抛出。带精确位置，便于上层渲染出错点 */
export class LexError extends Error {
  constructor(
    message: string,
    public readonly span: Span,
  ) {
    super(message);
    this.name = 'LexError';
  }

  toEvalError(): EvalError {
    return { kind: 'LexError', message: this.message, pos: this.span };
  }
}

const isDigit = (ch: string): boolean => ch >= '0' && ch <= '9';
const isIdentStart = (ch: string): boolean =>
  (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_' || ch === '$';
const isIdentPart = (ch: string): boolean => isIdentStart(ch) || isDigit(ch);
const isSpace = (ch: string): boolean =>
  ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';

/**
 * 把源码切成 token 数组。
 *
 * 不合法字符会直接抛 LexError —— 因为 IL 是给机器生成的，
 * 出现奇怪字符说明上游（提示词或清洗）出了问题，
 * 静默跳过只会让错误更靠后、更难查。
 */
export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = source.length;

  while (i < n) {
    const ch = source[i];

    // ── 空白 ──
    if (isSpace(ch)) {
      i++;
      continue;
    }

    // ── 注释：整行跳过到行尾。清洗层通常已经剥掉了，这里兜底 ──
    if (ch === '#') {
      while (i < n && source[i] !== '\n') i++;
      continue;
    }
    if (ch === '/' && source[i + 1] === '/') {
      while (i < n && source[i] !== '\n') i++;
      continue;
    }

    // ── 字符串 ──
    if (ch === "'" || ch === '"') {
      const quote = ch;
      const start = i;
      i++; // 跳过开引号
      let value = '';
      let closed = false;

      while (i < n) {
        const c = source[i];

        if (c === '\\') {
          // 转义序列。IL 只认这几种，其余原样保留（不猜）
          const next = source[i + 1];
          if (next === undefined) {
            throw new LexError('字符串末尾的反斜杠没有内容', { start, end: i + 1 });
          }
          value += unescape(next);
          i += 2;
          continue;
        }

        if (c === quote) {
          closed = true;
          i++;
          break;
        }

        // 换行不允许出现在字符串里（IL 用 ; 分隔语句，换行没有语义）
        if (c === '\n') {
          throw new LexError('字符串没有闭合就换行了', { start, end: i });
        }

        value += c;
        i++;
      }

      if (!closed) {
        throw new LexError('字符串没有闭合', { start, end: n });
      }

      tokens.push({
        type: 'string',
        raw: source.slice(start, i),
        value,
        start,
        end: i,
      });
      continue;
    }

    // ── 数字（含负号） ──
    // 注意：只把「负号 + 数字」当负数。因为 IL 里没有减法运算，
    // 出现 `-` 必然是数字的一部分，不会与运算符歧义。
    if (isDigit(ch) || (ch === '-' && isDigit(source[i + 1] ?? ''))) {
      const start = i;
      if (ch === '-') i++;
      while (i < n && (isDigit(source[i]) || source[i] === '.')) i++;

      const raw = source.slice(start, i);
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        throw new LexError(`无法解析的数字「${raw}」`, { start, end: i });
      }

      tokens.push({ type: 'number', raw, value: num, start, end: i });
      continue;
    }

    // ── 标识符 / 关键字 ──
    if (isIdentStart(ch)) {
      const start = i;
      while (i < n && isIdentPart(source[i])) i++;
      const raw = source.slice(start, i);

      if (raw === 'true' || raw === 'false') {
        tokens.push({ type: 'boolean', raw, value: raw === 'true', start, end: i });
      } else {
        tokens.push({ type: 'ident', raw, value: raw, start, end: i });
      }
      continue;
    }

    // ── 标点 ──
    const punct: Record<string, TokenType> = {
      '.': 'dot',
      '(': 'lparen',
      ')': 'rparen',
      ',': 'comma',
      ';': 'semi',
    };
    const type = punct[ch];
    if (type) {
      tokens.push({ type, raw: ch, start: i, end: i + 1 });
      i++;
      continue;
    }

    // ── 不认识 ──
    throw new LexError(`无法识别的字符「${ch}」`, { start: i, end: i + 1 });
  }

  tokens.push({ type: 'eof', raw: '', start: n, end: n });
  return tokens;
}

/** 转义字符还原。不认识的转义原样返回，不做猜测 */
function unescape(ch: string): string {
  switch (ch) {
    case 'n':
      return '\n';
    case 't':
      return '\t';
    case 'r':
      return '\r';
    case '\\':
      return '\\';
    case "'":
      return "'";
    case '"':
      return '"';
    default:
      return ch;
  }
}
